'use client';

import type {
  BoardAgentAction,
  BoardAgentActionMap,
  BoardAgentEventMap,
  TerminalClientMessage
} from '@shared/all/domains/electron';
import { useEffect, useState } from 'react';

// ── Window.electronAPI declaration ──

declare global {
  interface Window {
    electronAPI?: {
      isElectron: true;
      platform: string;
      goBack: () => void;
      goForward: () => void;
      showNotification: (title: string, body: string, iconUrl?: string) => void;
      setBadgeCount: (count: number) => void;
      openExternal: (url: string) => void;
      onDeepLink: (callback: (url: string) => void) => () => void;

      boardAgent: {
        dispatch: <A extends BoardAgentAction>(
          spaceSlug: string,
          action: A,
          ...args: BoardAgentActionMap[A]['payload'] extends void
            ? []
            : [payload: BoardAgentActionMap[A]['payload']]
        ) => Promise<BoardAgentActionMap[A]['result']>;

        on: <E extends keyof BoardAgentEventMap>(
          spaceSlug: string,
          event: E,
          callback: (data: BoardAgentEventMap[E]) => void
        ) => () => void;
      };

      terminal: {
        send: (msg: TerminalClientMessage) => void;
        onFrame: (callback: (frame: string) => void) => () => void;
      };

      // Optional: the web app ships continuously but the desktop shell updates
      // on its own cadence, so a running shell can predate any namespace added
      // here. Shells older than 1.6.0 have no `onedrive` at all — reaching into
      // it unguarded took the whole app down to the root error boundary.
      onedrive?: {
        onMintRequest: (callback: (spaceSlug: string) => void) => () => void;
        registerWebhook: (payload: {
          spaceSlug: string;
          notificationUrl: string;
          clientState: string;
        }) => void;
        changed: (spaceSlug: string) => void;
      };
    };
  }
}

const useIsElectron = () => {
  const [isElectron, setIsElectron] = useState(false);

  useEffect(() => {
    setIsElectron(Boolean(window.electronAPI?.isElectron));
  }, []);

  return isElectron;
};

export default useIsElectron;
